import { db } from "@/firebase";
import type { Account } from "@aircap/common/account";
import {
  decodeAuthClaims,
  hasRole,
  resolveAuthRole,
  type AuthKind,
  type AuthRole,
  type DecodedAuthClaims,
} from "@aircap/common/auth";
import type { SupervisorOrgUser } from "@aircap/common/supervisorOrg";
import { supervisorOrgUserFullPath } from "@aircap/common/supervisorOrg";
import { useLocalStorage, type RemovableRef } from "@vueuse/core";
import { type User as FirebaseUser, type IdTokenResult } from "firebase/auth";
import { doc, onSnapshot, type Unsubscribe } from "firebase/firestore";
import { defineStore } from "pinia";
import { watch } from "vue";

type AuthenticatedUser = {
  firebaseUser: FirebaseUser;
  authClaims: DecodedAuthClaims;
  idToken: IdTokenResult;
  supervisorOrgUser: SupervisorOrgUser | undefined;
};

let unsubscribeSupervisorOrgUser: Unsubscribe | undefined;

export const useUserStore = defineStore("user", {
  state: (): {
    resetKeyAvatar: number;
    _adminSupervisorOrgId: RemovableRef<string | null>;
    session:
      { status: "loading" } | { status: "anonymous" } | { status: "in"; user: AuthenticatedUser };
  } => ({
    resetKeyAvatar: 0,
    _adminSupervisorOrgId: useLocalStorage<string | null>("pinia/user/adminSupervisorOrgId", null),
    session: { status: "loading" },
  }),
  getters: {
    authenticatedUser(): undefined | null | AuthenticatedUser {
      switch (this.session.status) {
        case "loading":
          return undefined;
        case "anonymous":
          return null;
        case "in":
          return this.session.user;
      }
    },
    isLoading(): boolean {
      return this.session.status === "loading";
    },
    uid(): string | undefined {
      return this.session.status === "in" ? this.session.user.firebaseUser.uid : undefined;
    },
    supervisorOrgId(): string | null {
      if (this.isKind("admin") && this._adminSupervisorOrgId) return this._adminSupervisorOrgId;
      if (this.session.status !== "in" || this.session.user.authClaims.kind !== "supervisor")
        return null;
      return this.session.user.authClaims.supervisorOrgId;
    },
    isKind:
      (state) =>
      (kind: AuthKind): boolean =>
        state.session.status === "in" && state.session.user.authClaims.kind === kind,
    /**
     * `"supervisorOrg"` resolves the supervisor org role, which never matches `user.*` roles. `{ account }`
     * resolves the role on that account, and grants nothing while it is not loaded.
     */
    hasRole:
      (state) =>
      (
        roles: readonly AuthRole[],
        scope:
          "supervisorOrg" | { account: (Account & { readonly id: string }) | null | undefined },
      ): boolean => {
        if (state.session.status !== "in") return false;
        let account;
        if (scope !== "supervisorOrg") {
          if (!scope.account) return false;
          account = { id: scope.account.id, data: scope.account };
        }
        const authRole = resolveAuthRole({
          claims: state.session.user.authClaims,
          uid: state.session.user.firebaseUser.uid,
          supervisorOrgUser: state.session.user.supervisorOrgUser,
          account,
        });
        return hasRole(authRole, roles);
      },
  },
  actions: {
    async waitForLoading() {
      if (!this.isLoading) return;
      return new Promise<void>((resolve) => {
        const stop = watch(
          () => this.isLoading,
          (isLoading) => {
            if (!isLoading) {
              stop();
              resolve();
            }
          },
        );
      });
    },
    setAdminSupervisorOrgId(supervisorOrgId: string | null) {
      if (!this.isKind("admin")) throw new Error("Only admin users can set supervisorOrgId");
      this._adminSupervisorOrgId = supervisorOrgId;
    },
    async authenticate(firebaseUser: FirebaseUser) {
      const idToken = await firebaseUser.getIdTokenResult();
      const authClaims = decodeAuthClaims(idToken.claims);
      unsubscribeSupervisorOrgUser?.();
      unsubscribeSupervisorOrgUser = undefined;
      this.session = {
        status: "in",
        user: {
          firebaseUser,
          authClaims,
          idToken,
          supervisorOrgUser: await this._subscribeSupervisorOrgUser(firebaseUser.uid, authClaims),
        },
      };
    },
    async refresh() {
      if (this.session.status !== "in") throw new Error("User is not authenticated");
      const idToken = await this.session.user.firebaseUser.getIdTokenResult(true);
      const authClaims = decodeAuthClaims(idToken.claims);
      this.session.user.authClaims = authClaims;
      this.session.user.idToken = idToken;
      if (!unsubscribeSupervisorOrgUser)
        this.session.user.supervisorOrgUser = await this._subscribeSupervisorOrgUser(
          this.session.user.firebaseUser.uid,
          authClaims,
        );
    },
    async _subscribeSupervisorOrgUser(uid: string, authClaims: DecodedAuthClaims) {
      if (authClaims.kind !== "supervisor" || !authClaims.loggedInWithMfa) return undefined;
      return new Promise<SupervisorOrgUser | undefined>((resolve, reject) => {
        unsubscribeSupervisorOrgUser = onSnapshot(
          doc<SupervisorOrgUser>(db, supervisorOrgUserFullPath(authClaims.supervisorOrgId, uid)),
          (snapshot) => {
            const supervisorOrgUser = snapshot.data();
            if (this.session.status === "in")
              this.session.user.supervisorOrgUser = supervisorOrgUser;
            resolve(supervisorOrgUser);
          },
          reject,
        );
      });
    },
    unAuthenticate() {
      unsubscribeSupervisorOrgUser?.();
      unsubscribeSupervisorOrgUser = undefined;
      this.session = { status: "anonymous" };
      this._adminSupervisorOrgId = null;
    },
  },
});

export const supervisorOrgAvatarUrls = new Map<string, string>();
